/**
 * Unified Event State Machine
 *
 * This utility provides a single source of truth for event/game state determination
 * used by both event-detail-page and KioskPage.
 *
 * State Flow:
 * loading → not_found (if no event)
 *         → waiting_unchecked_in (if booker exists but not checked in)
 *         → idling (baseline state, scan QR to start)
 *         → waiting_normal (next session scheduled)
 *         → waiting_imminent (15s before session)
 *         → playing (session active)
 *         → playing_endgame_countdown (10s before session end)
 *         → endgame_processing (session ended, processing highlights)
 *         → endgame_stats (showing results)
 *         → back to waiting_normal or idling
 *         → end_event
 */

import type { components } from "../apis/gen";

type Event = components["schemas"]["Event"];
type Session = components["schemas"]["Session"];

// =============================================================================
// State Types
// =============================================================================

export const EVENT_STATES = {
	/** Initial loading state - handled at component level */
	LOADING: "loading",
	/** Event not found or no data */
	NOT_FOUND: "not_found",
	/** Booker exists but user hasn't checked in yet - show check-in QR */
	WAITING_UNCHECKED_IN: "waiting_unchecked_in",
	/** Baseline state: scan to join/start a new game */
	IDLING: "idling",
	/** Next game is scheduled and waiting to start */
	WAITING_NORMAL: "waiting_normal",
	/** 15 seconds before session starts */
	WAITING_IMMINENT: "waiting_imminent",
	/** Session actively ongoing */
	PLAYING: "playing",
	/** 10 seconds before session ends */
	PLAYING_ENDGAME_COUNTDOWN: "playing_endgame_countdown",
	/** Session ended, processing highlights */
	ENDGAME_PROCESSING: "endgame_processing",
	/** Processing complete, showing stats */
	ENDGAME_STATS: "endgame_stats",
	/** Event completely ended */
	END_EVENT: "end_event",
} as const;

export type EventStateValue = (typeof EVENT_STATES)[keyof typeof EVENT_STATES];

// =============================================================================
// Configuration Constants
// =============================================================================

/** Seconds before session start to enter imminent state */
export const IMMINENT_THRESHOLD_SECONDS = 60;

/** Seconds before session end to enter endgame countdown state */
export const ENDGAME_COUNTDOWN_THRESHOLD_SECONDS = 10;

/** Minutes before event end to show end_event when no next session */
export const END_EVENT_THRESHOLD_MINUTES = 12;

/** How long to show stats after a game ends (if no next game is imminent) */
export const STATS_DISPLAY_DURATION_SECONDS = 30;

// =============================================================================
// State Result Interface
// =============================================================================

export interface EventStateResult {
	/** Current state of the event */
	state: EventStateValue;
	/** Countdown in seconds (for imminent/endgame countdown/waiting states) */
	countdown: number;
	/** Index of the current or relevant session */
	sessionIdx: number;
	/** Whether event is in active/playing states */
	isActiveState: boolean;
	/** Whether event is in waiting/scheduled states */
	isWaitingState: boolean;
	/** Whether event is in endgame states (processing, stats) */
	isEndgameState: boolean;
	/** Whether event is in playing states (playing, endgame_countdown) */
	isPlayingState: boolean;
	/** Whether event has completely ended */
	isEventEnded: boolean;
	/** Whether there's a next session that hasn't started */
	hasNextSession: boolean;
	/** Whether adding a new session is allowed */
	canAddSession: boolean;
	/** Whether check-in is required (booker exists but not checked in) */
	requiresCheckIn: boolean;
}

// =============================================================================
// Main State Determination Function
// =============================================================================

/**
 * Determines the current event state based on event data and current time.
 */
export function determineEventState(
	event: Event | null | undefined,
	currentTimestamp: number = Date.now(),
): EventStateResult {
	const defaultResult: EventStateResult = {
		state: EVENT_STATES.NOT_FOUND,
		countdown: 0,
		sessionIdx: -1,
		isActiveState: false,
		isWaitingState: false,
		isEndgameState: false,
		isPlayingState: false,
		isEventEnded: false,
		hasNextSession: false,
		canAddSession: false,
		requiresCheckIn: false,
	};

	if (!event) return defaultResult;

	const now = currentTimestamp;
	const eventEndTime = event.ended_at
		? new Date(event.ended_at).getTime()
		: Infinity;

	// 1. Check if event has completely ended
	if (now >= eventEndTime) {
		return {
			...defaultResult,
			state: EVENT_STATES.END_EVENT,
			isEventEnded: true,
		};
	}

	// 2. Check if check-in is required (BLOCKING STATE)
	const requiresCheckIn = !!event.booker && !event.checked_in_at;
	if (requiresCheckIn) {
		// Find the next upcoming session so the check-in screen shows correct "UP NEXT" info
		const sessions = event.sessions || [];
		let nextSessionIdx = -1;
		let secondsToNext = 0;
		for (let i = 0; i < sessions.length; i++) {
			const start = new Date(sessions[i].started_at).getTime();
			if (now < start) {
				nextSessionIdx = i;
				secondsToNext = Math.max(0, Math.floor((start - now) / 1000));
				break;
			}
		}

		return {
			...defaultResult,
			state: EVENT_STATES.WAITING_UNCHECKED_IN,
			isWaitingState: true,
			requiresCheckIn: true,
			sessionIdx: nextSessionIdx >= 0 ? nextSessionIdx : 0,
			countdown: secondsToNext,
			hasNextSession: nextSessionIdx >= 0,
		};
	}

	// 3. Process sessions
	const sessions = event.sessions || [];
	let activeSessionIdx = -1;
	let nextSessionIdx = -1;
	let lastEndedSessionIdx = -1;

	for (let i = 0; i < sessions.length; i++) {
		const session = sessions[i];
		const start = new Date(session.started_at).getTime();
		const end = new Date(session.ended_at).getTime();

		if (now >= start && now < end) {
			activeSessionIdx = i;
			break;
		} else if (now < start && nextSessionIdx === -1) {
			nextSessionIdx = i;
		} else if (now >= end) {
			lastEndedSessionIdx = i;
		}
	}

	const hasNextSession = nextSessionIdx !== -1;

	// State: PLAYING or PLAYING_ENDGAME_COUNTDOWN
	if (activeSessionIdx !== -1) {
		const end = new Date(sessions[activeSessionIdx].ended_at).getTime();
		const secondsLeft = Math.max(0, Math.floor((end - now) / 1000));
		const isEnding = secondsLeft <= ENDGAME_COUNTDOWN_THRESHOLD_SECONDS;

		return {
			...defaultResult,
			state: isEnding
				? EVENT_STATES.PLAYING_ENDGAME_COUNTDOWN
				: EVENT_STATES.PLAYING,
			countdown: secondsLeft,
			sessionIdx: activeSessionIdx,
			isActiveState: true,
			isPlayingState: true,
			hasNextSession,
		};
	}

	// Pre-calculate next session timing to determine imminence
	let nextIsImminent = false;
	let secondsToNext = 0;
	if (hasNextSession) {
		const nextStart = new Date(sessions[nextSessionIdx].started_at).getTime();
		secondsToNext = Math.max(0, Math.floor((nextStart - now) / 1000));
		nextIsImminent = secondsToNext <= IMMINENT_THRESHOLD_SECONDS;
	}

	// State: WAITING (Imminent)
	// Higher priority than endgame states — Rule 3
	if (nextIsImminent) {
		return {
			...defaultResult,
			state: EVENT_STATES.WAITING_IMMINENT,
			countdown: secondsToNext,
			sessionIdx: nextSessionIdx,
			isActiveState: true,
			isWaitingState: true,
			hasNextSession: true,
		};
	}

	// State: ENDGAME (Processing or Stats)
	if (lastEndedSessionIdx !== -1) {
		const lastSession = sessions[lastEndedSessionIdx];
		const isProcessing = (lastSession as any).is_processing;
		const isArchived = (lastSession as any).is_archived;
		const end = new Date(lastSession.ended_at).getTime();
		const secondsSinceEnd = Math.floor((now - end) / 1000);

		// Stay in processing until we get a positive "done" signal:
		// is_archived (from session_archived SSE) or is_processing explicitly false
		// after being set true by the backend (on refetch).
		// During live play, is_processing starts false and is_archived is unset,
		// so we treat !isArchived as "still processing" for recently ended sessions.
		if (isProcessing || !isArchived) {
			return {
				...defaultResult,
				state: EVENT_STATES.ENDGAME_PROCESSING,
				sessionIdx: lastEndedSessionIdx,
				isEndgameState: true,
				hasNextSession,
			};
		}

		// Show stats for a duration. Transition to waiting happens naturally
		// when either stats window expires OR next game becomes imminent (handled above).
		const statsWindowActive = secondsSinceEnd < STATS_DISPLAY_DURATION_SECONDS;

		if (statsWindowActive) {
			return {
				...defaultResult,
				state: EVENT_STATES.ENDGAME_STATS,
				sessionIdx: lastEndedSessionIdx,
				isEndgameState: true,
				hasNextSession,
				canAddSession: !hasNextSession,
			};
		}
	}

	// State: WAITING (Normal)
	if (hasNextSession) {
		const start = new Date(sessions[nextSessionIdx].started_at).getTime();
		const secondsLeft = Math.max(0, Math.floor((start - now) / 1000));
		// isImminent check is technically redundant here due to priority check above,
		// but kept for logic clarity.
		const isImminent = secondsLeft <= IMMINENT_THRESHOLD_SECONDS;

		return {
			...defaultResult,
			state: isImminent
				? EVENT_STATES.WAITING_IMMINENT
				: EVENT_STATES.WAITING_NORMAL,
			countdown: secondsLeft,
			sessionIdx: nextSessionIdx,
			isActiveState: true,
			isWaitingState: true,
			hasNextSession: true,
		};
	}

	// State: IDLING or END_EVENT (based on time remaining)
	const minutesToEnd = (eventEndTime - now) / (1000 * 60);
	if (minutesToEnd <= END_EVENT_THRESHOLD_MINUTES) {
		return {
			...defaultResult,
			state: EVENT_STATES.END_EVENT,
			isEventEnded: true,
		};
	}

	return {
		...defaultResult,
		state: EVENT_STATES.IDLING,
		isActiveState: false, // Detail page shows HeroCheckin
		isWaitingState: false,
		canAddSession: true,
	};
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format countdown seconds to MM:SS string, or SS when under one minute
 */
export function formatCountdown(seconds: number): string {
	if (seconds < 60) {
		return seconds.toString().padStart(2, "0");
	}
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Map unified state to Kiosk stage/subStage format
 */
export function mapToKioskState(state: EventStateValue): {
	stage: "idle" | "waiting" | "playing" | "endgame";
	subStage:
		| "normal"
		| "imminent"
		| "checkin"
		| "regular"
		| "ending"
		| "processing"
		| "stats"
		| "all_processed"
		| null;
} {
	switch (state) {
		case EVENT_STATES.NOT_FOUND:
		case EVENT_STATES.END_EVENT:
			return { stage: "idle", subStage: null };

		case EVENT_STATES.WAITING_UNCHECKED_IN:
			return { stage: "waiting", subStage: "checkin" };

		case EVENT_STATES.IDLING:
			return { stage: "idle", subStage: null };

		case EVENT_STATES.WAITING_NORMAL:
			return { stage: "waiting", subStage: "normal" };

		case EVENT_STATES.WAITING_IMMINENT:
			return { stage: "waiting", subStage: "imminent" };

		case EVENT_STATES.PLAYING:
			return { stage: "playing", subStage: "regular" };

		case EVENT_STATES.PLAYING_ENDGAME_COUNTDOWN:
			return { stage: "playing", subStage: "ending" };

		case EVENT_STATES.ENDGAME_PROCESSING:
			return { stage: "endgame", subStage: "processing" };

		case EVENT_STATES.ENDGAME_STATS:
			return { stage: "endgame", subStage: "all_processed" };

		default:
			return { stage: "idle", subStage: null };
	}
}
